import React from 'react';
import { View, Text, TouchableOpacity, ScrollView, StyleSheet } from 'react-native';
import { Link, useLocation } from 'react-router-dom';
import { useAccounts, useLiabilities } from '../../hooks/useData';

const navLinks = [
  { name: 'Home', icon: '🏠', path: '/' },
  { name: 'Income', icon: '💵', path: '/income' },
  { name: 'Spending', icon: '💸', path: '/spending' },
  { name: 'Budget', icon: '📊', path: '/budget' },
  { name: 'Accounts', icon: '🏦', path: '/accounts' },
  { name: 'Transactions', icon: '📄', path: '/transactions' },
  { name: 'Protection', icon: '🛡️', path: '/protection' },
  { name: 'Assets', icon: '📘', path: '/assets' },
  { name: 'Liabilities', icon: '📕', path: '/liabilities' },
  { name: 'Cash Flow', icon: '💚', path: '/cashflow' },
  { name: 'Wealth Plan', icon: '📈', path: '/wealth-plan' },
  { name: 'Fact Finder', icon: '📝', path: '/fact-finder' },
  { name: 'Plaid Test', icon: '🔗', path: '/plaid-test' },
];

const groupAccountsByType = (accounts, liabilities) => {
  const groups = {
    CASH: [],
    CREDIT: [],
    LOANS: [],
    TRACKING: [],
    CLOSED: []
  };

  // Process accounts
  accounts?.forEach(account => {
    switch (account.type) {
      case 'checking':
      case 'savings':
        groups.CASH.push({
          name: account.name.includes('Checking') ? 'Checking' : 
                account.name.includes('Savings') ? 'Savings' : account.name,
          balance: account.balance
        });
        break;
      case 'brokerage':
      case 'retirement':
      case 'crypto':
        groups.TRACKING.push({
          name: account.type === 'retirement' ? 'IRA' : 
                account.type === 'crypto' ? 'Crypto' : 'Brokerage',
          balance: account.balance
        });
        break;
      default:
        break;
    }
  });

  // Process liabilities
  liabilities?.forEach(liability => {
    switch (liability.type) {
      case 'credit_card':
        groups.CREDIT.push({
          name: liability.name?.includes('Sapphire') ? 'Visa' : 'Credit Card',
          balance: -liability.balance // Negative because it's debt
        });
        break;
      case 'student_loan':
        groups.LOANS.push({
          name: 'Student Loan',
          balance: -liability.balance
        });
        break;
      case 'auto_loan':
        groups.LOANS.push({
          name: 'Auto Loan',
          balance: -liability.balance
        });
        break;
      default:
        break;
    }
  });

  return Object.entries(groups).map(([label, accounts]) => ({
    label,
    accounts
  }));
};

// Mobile-specific Link wrapper component
const MobileNavLink = ({ to, isActive, children, onPress }) => {
  return (
    <Link to={to} style={{ textDecoration: 'none' }}>
      <TouchableOpacity 
        style={[styles.navItem, isActive && styles.navItemActive]} 
        onPress={onPress}
      >
        {children}
      </TouchableOpacity>
    </Link>
  );
};

const MobileSidebar = ({ isOpen, onClose }) => {
  const location = useLocation();
  const { data: accounts, loading: accountsLoading } = useAccounts();
  const { data: liabilities, loading: liabilitiesLoading } = useLiabilities();
  
  const loading = accountsLoading || liabilitiesLoading;
  const accountGroups = loading ? [] : groupAccountsByType(accounts, liabilities);

  if (!isOpen) return null;

  return (
    <View style={styles.overlay}>
      {/* Background overlay */}
      <TouchableOpacity style={styles.backdrop} onPress={onClose} />
      
      {/* Sidebar content */}
      <View style={styles.sidebar}>
        {/* Header with close button */}
        <View style={styles.header}>
          <View style={styles.logoContainer}>
            <img 
              src="/zecher-logo.png" 
              alt="Zecher"
              style={styles.logoImage}
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
            <View style={[styles.logoFallback, { display: 'none' }]}>
              <View style={styles.logoIcon}>
                <Text style={styles.logoText}>Z</Text>
              </View>
              <Text style={styles.logoTitle}>echer</Text>
            </View>
          </View>
          
          <TouchableOpacity style={styles.closeButton} onPress={onClose}>
            <Text style={styles.closeButtonText}>✕</Text>
          </TouchableOpacity>
        </View>

        <ScrollView style={styles.scrollView} showsVerticalScrollIndicator={false}>
          <View style={styles.content}>
            {/* Navigation Links */}
            <View style={styles.navigation}>
              {navLinks.map((link) => {
                const isActive = location.pathname === link.path;
                return (
                  <MobileNavLink 
                    key={link.name} 
                    to={link.path} 
                    isActive={isActive}
                    onPress={onClose} // Close sidebar when nav item is pressed
                  >
                    <Text style={styles.navIcon}>{link.icon}</Text>
                    <Text style={[
                      styles.navText, 
                      isActive ? styles.navTextActive : styles.navTextDefault
                    ]}>
                      {link.name}
                    </Text>
                  </MobileNavLink>
                );
              })}
            </View>
            
            {/* Account Groups */}
            <View style={styles.accountSection}>
              {loading ? (
                <Text style={styles.loadingText}>Loading accounts...</Text>
              ) : (
                accountGroups.map((group) => (
                  <View key={group.label} style={styles.accountGroup}>
                    <Text style={styles.groupLabel}>{group.label}</Text>
                    <View style={styles.accountList}>
                      {group.accounts.length === 0 ? (
                        <Text style={styles.noAccountsText}>No accounts</Text>
                      ) : (
                        group.accounts.map((acc, index) => (
                          <View key={`${acc.name}-${index}`} style={styles.accountItem}>
                            <Text style={styles.accountName}>{acc.name}</Text>
                            <Text style={[
                              styles.accountBalance,
                              acc.balance < 0 ? styles.negativeBalance : styles.positiveBalance
                            ]}>
                              {acc.balance < 0 ? '-' : ''}${Math.abs(acc.balance).toLocaleString()}
                            </Text>
                          </View>
                        ))
                      )}
                    </View>
                  </View>
                ))
              )}
            </View>
          </View>
        </ScrollView>
        
        {/* Add Account Button */}
        <TouchableOpacity style={styles.addButton}>
          <Text style={styles.addButtonText}>+ Add Account</Text>
        </TouchableOpacity>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 100,
    flexDirection: 'row',
  },
  backdrop: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
  },
  sidebar: {
    width: 280,
    backgroundColor: '#FFFFFF',
    height: '100%',
    shadowColor: '#000',
    shadowOffset: { width: 2, height: 0 },
    shadowOpacity: 0.25,
    shadowRadius: 8,
    elevation: 8,
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 24,
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#F3F4F6',
  },
  logoContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  logoImage: {
    height: 32,
    width: 'auto',
    maxWidth: 150,
    objectFit: 'contain',
  },
  logoFallback: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  logoIcon: {
    width: 32,
    height: 32,
    backgroundColor: '#2563EB',
    borderRadius: 6,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 8,
  },
  logoText: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#FFFFFF',
  },
  logoTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#111827',
  },
  closeButton: {
    width: 32,
    height: 32,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 6,
  },
  closeButtonText: {
    fontSize: 20,
    color: '#6B7280',
    fontWeight: 'bold',
  },
  scrollView: {
    flex: 1,
  },
  content: {
    paddingHorizontal: 24,
    paddingTop: 16,
    paddingBottom: 8,
  },
  navigation: {
    marginBottom: 24,
  },
  navItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 12,
    borderRadius: 8,
    marginBottom: 4,
    minHeight: 48, // Better touch target
  },
  navItemActive: {
    backgroundColor: '#EFF6FF',
  },
  navIcon: {
    fontSize: 20,
    marginRight: 12,
    width: 24,
    textAlign: 'center',
  },
  navText: {
    fontSize: 16,
    fontWeight: '500',
    flex: 1,
  },
  navTextDefault: {
    color: '#374151',
  },
  navTextActive: {
    color: '#2563EB',
  },
  accountSection: {
    flex: 1,
  },
  accountGroup: {
    marginBottom: 12,
  },
  groupLabel: {
    fontSize: 12,
    color: '#9CA3AF',
    marginBottom: 6,
    fontWeight: '500',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  accountList: {
    marginBottom: 4,
  },
  accountItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 4,
    paddingVertical: 4,
  },
  accountName: {
    fontSize: 14,
    color: '#374151',
    flex: 1,
  },
  accountBalance: {
    fontSize: 14,
    fontWeight: '600',
    textAlign: 'right',
  },
  positiveBalance: {
    color: '#059669',
  },
  negativeBalance: {
    color: '#DC2626',
  },
  noAccountsText: {
    fontSize: 12,
    color: '#D1D5DB',
    fontStyle: 'italic',
  },
  loadingText: {
    fontSize: 12,
    color: '#9CA3AF',
  },
  addButton: {
    margin: 24,
    marginTop: 16,
    paddingVertical: 12,
    borderRadius: 8,
    backgroundColor: '#EFF6FF',
    alignItems: 'center',
    minHeight: 48, // Better touch target
  },
  addButtonText: {
    color: '#2563EB',
    fontSize: 16,
    fontWeight: '600',
  },
});

export default MobileSidebar; 